import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Check,
  Edit2,
  Loader2,
  Plus,
  Save,
  X,
} from 'lucide-react';
import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useToast } from '../../hooks/useToast';
import { partnerApi, type Tenant } from '../../lib/api';

interface TenantSmsTabProps {
  tenant: Tenant;
}

export const TenantSmsTab: React.FC<TenantSmsTabProps> = ({ tenant }) => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [smsOptInUrls, setSmsOptInUrls] = useState<string[]>(
    tenant.smsOptInImageUrls || [],
  );
  const [businessWebsite, setBusinessWebsite] = useState<string>(
    tenant.website || '',
  );

  /* Fetch SMS registration status separately */
  const { data: smsRegistrationStatus } = useQuery({
    queryKey: ['tenant-sms-registration', tenant.id],
    queryFn: () => partnerApi.getTenantSmsRegistrationStatus(tenant.id),
  });

  const updateMutation = useMutation({
    mutationFn: (data: { smsOptInImageUrls?: string[]; website?: string }) =>
      partnerApi.updateTenant(tenant.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id] });
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      addToast({
        variant: 'success',
        title: 'SMS information updated successfully',
      });
      setIsEditing(false);
    },
    onError: error => {
      addToast({
        variant: 'destructive',
        title: 'Failed to update SMS information',
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });

  const handleAddUrl = () => {
    setSmsOptInUrls([...smsOptInUrls, '']);
  };

  const handleRemoveUrl = (index: number) => {
    const newUrls = smsOptInUrls.filter((_, i) => i !== index);
    setSmsOptInUrls(newUrls);
  };

  const handleUpdateUrl = (index: number, value: string) => {
    const newUrls = [...smsOptInUrls];
    newUrls[index] = value;
    setSmsOptInUrls(newUrls);
  };

  const normalizeUrl = (url: string): string => {
    const trimmed = url.trim();
    if (!trimmed) return trimmed;
    // Add https:// if no protocol is specified
    if (!/^https?:\/\//i.test(trimmed)) {
      return `https://${trimmed}`;
    }
    return trimmed;
  };

  const handleSave = () => {
    // Filter out empty URLs and normalize them
    const validUrls = smsOptInUrls
      .filter(url => url.trim() !== '')
      .map(normalizeUrl);

    const normalizedWebsite = businessWebsite.trim()
      ? normalizeUrl(businessWebsite)
      : undefined;

    updateMutation.mutate({
      smsOptInImageUrls: validUrls.length > 0 ? validUrls : undefined,
      website: normalizedWebsite,
    });
  };

  const handleCancel = () => {
    // Reset to original values
    setSmsOptInUrls(tenant.smsOptInImageUrls || []);
    setBusinessWebsite(tenant.website || '');
    setIsEditing(false);
  };

  const appData = smsRegistrationStatus?.application as
    | {
        type?: string;
        company?: {
          name?: string;
          address1?: string;
          address2?: string;
          city?: string;
          stateProvince?: string;
          zipCode?: string;
          country?: string;
        };
        contact?: {
          firstName?: string;
          lastName?: string;
          email?: string;
          phoneNumber?: string;
        };
        messagingUseCase?: {
          category?: string;
          useCaseDetails?: string;
          monthlyVolume?: number;
        };
        messageSamples?: string[];
        tollFreeNumber?: string;
        [key: string]: unknown;
      }
    | null
    | undefined;

  return (
    <div className="max-w-4xl space-y-6">
      {/* Edit/View Toggle Button */}
      {!isEditing && (
        <div className="flex justify-end">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setIsEditing(true)}>
            <Edit2 className="w-4 h-4 mr-2" />
            Edit SMS Information
          </Button>
        </div>
      )}

      {/* Business Website */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-semibold leading-7 text-gray-900 mb-4">
          Business Website
        </h3>
        {isEditing ? (
          <div className="space-y-2">
            <Input
              type="text"
              value={businessWebsite}
              onChange={e => setBusinessWebsite(e.target.value)}
              placeholder="https://example.com"
            />
            <p className="text-xs text-gray-500">
              This website will be used for SMS registration applications
            </p>
          </div>
        ) : (
          <div className="text-sm text-gray-900">
            {tenant.website ? (
              <a
                href={tenant.website}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline">
                {tenant.website}
              </a>
            ) : (
              <span className="text-gray-500 italic">
                No website configured
              </span>
            )}
          </div>
        )}
      </div>

      {/* SMS Registration Status Section */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-semibold leading-7 text-gray-900 mb-4">
          SMS Registration Status
        </h3>

        {smsRegistrationStatus?.status ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-sm font-medium text-gray-700">
                    Status:
                  </span>
                  <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border">
                    {smsRegistrationStatus.status === 'approved' ? (
                      <>
                        <Check className="w-4 h-4 mr-1.5 text-green-600" />
                        <span className="text-green-700">Approved</span>
                      </>
                    ) : smsRegistrationStatus.status === 'rejected' ? (
                      <>
                        <X className="w-4 h-4 mr-1.5 text-red-600" />
                        <span className="text-red-700">Rejected</span>
                      </>
                    ) : smsRegistrationStatus.status === 'in_review' ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-1.5 text-yellow-600 animate-spin" />
                        <span className="text-yellow-700">In Review</span>
                      </>
                    ) : (
                      <>
                        <AlertTriangle className="w-4 h-4 mr-1.5 text-gray-600" />
                        <span className="text-gray-700">
                          {smsRegistrationStatus.status
                            .replace('_', ' ')
                            .toUpperCase()}
                        </span>
                      </>
                    )}
                  </div>
                </div>

                {smsRegistrationStatus.id && (
                  <div className="text-xs text-gray-500 mb-1">
                    <span className="font-medium">Application ID:</span>{' '}
                    {smsRegistrationStatus.id}
                  </div>
                )}

                {smsRegistrationStatus.createdAt && (
                  <div className="text-xs text-gray-500">
                    <span className="font-medium">Created:</span>{' '}
                    {new Date(smsRegistrationStatus.createdAt).toLocaleString()}
                  </div>
                )}

                {smsRegistrationStatus.updatedAt && (
                  <div className="text-xs text-gray-500">
                    <span className="font-medium">Last updated:</span>{' '}
                    {new Date(smsRegistrationStatus.updatedAt).toLocaleString()}
                  </div>
                )}
              </div>
            </div>

            {smsRegistrationStatus.status === 'rejected' && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">
                  <span className="font-semibold">Note:</span> This application
                  was rejected by Twilio. Please review the opt-in URLs and
                  business information below, then contact support to resubmit.
                </p>
              </div>
            )}

            {smsRegistrationStatus.status === 'in_review' && (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <span className="font-semibold">Note:</span> This application
                  is currently being reviewed by Twilio. This typically takes
                  1-3 business days.
                </p>
              </div>
            )}

            {smsRegistrationStatus.status === 'approved' && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800">
                  <span className="font-semibold">Note:</span> This application
                  has been approved by Twilio. SMS messaging is enabled for this
                  tenant.
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="p-4 bg-gray-50 rounded-lg border text-sm text-gray-500">
            No SMS registration application submitted yet. Add opt-in URLs below
            and the system will automatically submit an application.
          </div>
        )}
      </div>

      {/* Twilio Number (if approved) */}
      {smsRegistrationStatus?.status === 'approved' &&
        appData?.tollFreeNumber && (
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-semibold leading-7 text-gray-900 mb-4">
              Twilio Phone Number
            </h3>
            <div className="text-sm text-gray-900 font-mono">
              {appData.tollFreeNumber}
            </div>
          </div>
        )}

      {/* SMS Opt-in URLs Section */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="mb-4">
          <h3 className="text-lg font-semibold leading-7 text-gray-900">
            SMS Opt-in Image URLs
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            URLs showing how customers consent to receive SMS messages (required
            for Twilio SMS registration approval)
          </p>
        </div>

        {isEditing ? (
          <>
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-gray-700">Edit URLs</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddUrl}>
                <Plus className="w-4 h-4 mr-1" />
                Add URL
              </Button>
            </div>

            <div className="space-y-3 mb-4">
              {smsOptInUrls.length === 0 ? (
                <div className="p-4 bg-gray-50 rounded-lg border text-sm text-gray-500">
                  No SMS opt-in URLs configured. Click "Add URL" to add one.
                </div>
              ) : (
                smsOptInUrls.map((url, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      type="text"
                      value={url}
                      onChange={e => handleUpdateUrl(index, e.target.value)}
                      placeholder="https://example.com/consent-page or image URL"
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleRemoveUrl(index)}
                      className="h-10 px-3">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </>
        ) : (
          <div className="space-y-2">
            {tenant.smsOptInImageUrls && tenant.smsOptInImageUrls.length > 0 ? (
              tenant.smsOptInImageUrls.map((url, index) => (
                <div key={index} className="text-sm text-gray-900">
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline break-all">
                    {url}
                  </a>
                </div>
              ))
            ) : (
              <div className="text-sm text-gray-500 italic">
                No SMS opt-in URLs configured
              </div>
            )}
          </div>
        )}

        {!smsRegistrationStatus?.status &&
          !isEditing &&
          (!tenant.smsOptInImageUrls ||
            tenant.smsOptInImageUrls.length === 0) && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <span className="font-semibold">Info:</span> Once you add and
                save opt-in URLs, the system will automatically create and
                submit an SMS registration application to Twilio within a few
                hours.
              </p>
            </div>
          )}
      </div>

      {/* Application Data */}
      {appData && (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-semibold leading-7 text-gray-900 mb-4">
            Application Data
          </h3>
          <div className="space-y-4">
            {/* Company Information */}
            {appData.company && (
              <div className="border-b pb-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">
                  Company Information
                </h4>
                <dl className="space-y-2">
                  {appData.company.name && (
                    <div>
                      <dt className="text-xs font-medium text-gray-500">
                        Name
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {appData.company.name}
                      </dd>
                    </div>
                  )}
                  {(appData.company.address1 || appData.company.address2) && (
                    <div>
                      <dt className="text-xs font-medium text-gray-500">
                        Address
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {appData.company.address1}
                        {appData.company.address2 && (
                          <>, {appData.company.address2}</>
                        )}
                      </dd>
                    </div>
                  )}
                  {(appData.company.city ||
                    appData.company.stateProvince ||
                    appData.company.zipCode) && (
                    <div>
                      <dt className="text-xs font-medium text-gray-500">
                        Location
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {appData.company.city && `${appData.company.city}, `}
                        {appData.company.stateProvince}{' '}
                        {appData.company.zipCode}
                      </dd>
                    </div>
                  )}
                  {appData.company.country && (
                    <div>
                      <dt className="text-xs font-medium text-gray-500">
                        Country
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {appData.company.country}
                      </dd>
                    </div>
                  )}
                </dl>
              </div>
            )}

            {/* Contact Information */}
            {appData.contact && (
              <div className="border-b pb-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">
                  Contact Information
                </h4>
                <dl className="space-y-2">
                  {(appData.contact.firstName || appData.contact.lastName) && (
                    <div>
                      <dt className="text-xs font-medium text-gray-500">
                        Name
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {appData.contact.firstName} {appData.contact.lastName}
                      </dd>
                    </div>
                  )}
                  {appData.contact.email && (
                    <div>
                      <dt className="text-xs font-medium text-gray-500">
                        Email
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        <a
                          href={`mailto:${appData.contact.email}`}
                          className="text-primary hover:underline">
                          {appData.contact.email}
                        </a>
                      </dd>
                    </div>
                  )}
                  {appData.contact.phoneNumber && (
                    <div>
                      <dt className="text-xs font-medium text-gray-500">
                        Phone
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {appData.contact.phoneNumber}
                      </dd>
                    </div>
                  )}
                </dl>
              </div>
            )}

            {/* Messaging Use Case */}
            {appData.messagingUseCase && (
              <div className="border-b pb-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">
                  Messaging Use Case
                </h4>
                <dl className="space-y-2">
                  {appData.messagingUseCase.category && (
                    <div>
                      <dt className="text-xs font-medium text-gray-500">
                        Category
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {appData.messagingUseCase.category.replace(/_/g, ' ')}
                      </dd>
                    </div>
                  )}
                  {appData.messagingUseCase.useCaseDetails && (
                    <div>
                      <dt className="text-xs font-medium text-gray-500">
                        Details
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {appData.messagingUseCase.useCaseDetails}
                      </dd>
                    </div>
                  )}
                  {appData.messagingUseCase.monthlyVolume !== undefined && (
                    <div>
                      <dt className="text-xs font-medium text-gray-500">
                        Monthly Volume
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {appData.messagingUseCase.monthlyVolume} messages
                      </dd>
                    </div>
                  )}
                </dl>
              </div>
            )}

            {/* Message Samples */}
            {appData.messageSamples && appData.messageSamples.length > 0 && (
              <div className="border-b pb-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">
                  Message Samples
                </h4>
                <ul className="list-disc list-inside space-y-1">
                  {appData.messageSamples.map((sample: string, idx: number) => (
                    <li key={idx} className="text-sm text-gray-900">
                      {sample}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Application Type */}
            {appData.type && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">
                  Application Type
                </h4>
                <div className="text-sm text-gray-900">
                  {appData.type.replace(/_/g, ' ')}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Save/Cancel Buttons */}
      {isEditing && (
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={updateMutation.isPending}>
            <X className="w-4 h-4 mr-2" />
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={updateMutation.isPending}>
            {updateMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};
